import { Component, inject } from '@angular/core';
import {
  AbstractControl,
  FormControl,
  FormGroup,
  ValidationErrors,
  ValidatorFn,
  Validators
} from '@angular/forms';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { lastValueFrom } from 'rxjs';
import { GcpDatabaseUserControllerService } from 'src/app/admin-api';
import { AppDialogService } from 'src/app/services/dialog.service';
import { LoaderService } from 'src/app/services/loader.service';

@Component({
  selector: 'app-db-user-create-modal',
  templateUrl: './db-user-create-modal.component.html',
  styleUrls: ['./db-user-create-modal.component.scss']
})
export class DbUserCreateModalComponent {
  private dbUserService = inject(GcpDatabaseUserControllerService);
  user: { userId: number | null; displayName: string; username: string };
  dbUsername: string;

  form = new FormGroup(
    {
      password: new FormControl('', [
        Validators.required,
        Validators.minLength(8),
        this.uppercaseValidator(),
        this.numberValidator(),
        this.specialCharValidator()
      ]),
      passwordConfirmation: new FormControl('', [Validators.required])
    },
    { validators: this.passwordMatchValidator() }
  );

  constructor(
    private config: DynamicDialogConfig,
    public ref: DynamicDialogRef
  ) {
    this.user = this.config.data.user;
    this.dbUsername = this.user.username?.split('@')[0] || '';
  }

  async submit(): Promise<void> {
    if (this.form.invalid) return;
    LoaderService.showLoader();
    try {
      await lastValueFrom(
        this.dbUserService.createDatabaseUser(
          {
            password: this.form.value.password as string,
            passwordConfirmation: this.form.value.passwordConfirmation as string
          },
          this.user.userId as number
        )
      );
      this.ref.close(true);
    } catch (error) {
      AppDialogService.showErrorDialog(error);
    }
    LoaderService.showLoader(false);
  }

  private uppercaseValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!control.value) return null;
      return /[A-Z]/.test(control.value) ? null : { uppercase: true };
    };
  }

  private numberValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!control.value) return null;
      return /[0-9]/.test(control.value) ? null : { number: true };
    };
  }

  private specialCharValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!control.value) return null;
      return /[^A-Za-z0-9]/.test(control.value) ? null : { specialChar: true };
    };
  }

  private passwordMatchValidator(): ValidatorFn {
    return (group: AbstractControl): ValidationErrors | null => {
      const password = group.get('password')?.value;
      const confirmation = group.get('passwordConfirmation')?.value;
      return password === confirmation ? null : { mismatch: true };
    };
  }
}
